import { z } from 'zod';
import { objectId } from './schemas.js';
/**
 * The employment record behind each person: what they were hired as, who is
 * being hired next, and how the year's reviews went.
 *
 * The workspace already knows who reports to whom (the org chart), who is away
 * (leave) and what everybody has signed (policies). What it has never held is
 * the employment itself — job title, start date, the emergency contact
 * somebody has to be able to find at four in the afternoon. Those live here.
 *
 * Pay is deliberately not in this module. It sits in `payroll.ts` behind its
 * own permission, so an HR administrator can keep records without being handed
 * everyone's salary along with them.
 */
/* --------------------------- employee records ---------------------------- */
export const EMPLOYMENT_TYPES = ['full-time', 'part-time', 'contract', 'intern'];
/**
 * Where somebody is in their employment, which is not the same as whether
 * their account works. A person on garden leave still has a login; a member
 * who left still has records worth keeping.
 */
export const EMPLOYEE_STATUSES = ['active', 'probation', 'notice', 'left'];
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16');
const emergencyContactSchema = z.object({
    name: z.string().trim().min(2).max(120),
    relationship: z.string().trim().max(60).optional(),
    phone: z.string().trim().min(3).max(40),
});
export const createEmployeeRecordSchema = z.object({
    userId: objectId,
    employeeNumber: z.string().trim().max(40).optional(),
    jobTitle: z.string().trim().min(2).max(120),
    employmentType: z.enum(EMPLOYMENT_TYPES).default('full-time'),
    status: z.enum(EMPLOYEE_STATUSES).default('active'),
    startDate: dateOnly,
    probationEndDate: dateOnly.nullable().optional(),
    workLocation: z.string().trim().max(120).optional(),
    emergencyContact: emergencyContactSchema.nullable().optional(),
    note: z.string().trim().max(2000).optional(),
});
/** The person a record is about cannot move; everything else can. */
export const updateEmployeeRecordSchema = createEmployeeRecordSchema
    .omit({ userId: true })
    .partial();
/** Ending employment is its own act: it dates the record and states why. */
export const endEmploymentSchema = z.object({
    endDate: dateOnly,
    leavingReason: z.string().trim().max(300).optional(),
});
/**
 * What a person may see about their own employment, and edit.
 *
 * The emergency contact is theirs to keep current — it is the one field HR
 * cannot realistically maintain and the one most likely to be out of date.
 */
export const updateMyRecordSchema = z.object({
    emergencyContact: emergencyContactSchema.nullable().optional(),
    workLocation: z.string().trim().max(120).optional(),
});
/* ------------------------------ recruiting ------------------------------- */
export const OPENING_STATUSES = ['open', 'on-hold', 'closed'];
export const createOpeningSchema = z.object({
    title: z.string().trim().min(2).max(140),
    departmentId: objectId.nullable().optional(),
    employmentType: z.enum(EMPLOYMENT_TYPES).default('full-time'),
    location: z.string().trim().max(120).optional(),
    description: z.string().trim().max(8000).optional(),
    openings: z.number().int().min(1).max(500).default(1),
    status: z.enum(OPENING_STATUSES).default('open'),
    hiringManagerId: objectId.nullable().optional(),
});
export const updateOpeningSchema = createOpeningSchema.partial();
/**
 * The hiring funnel, in order. `hired` and `rejected` are the two ends, and
 * both are terminal: a candidate who reapplies is a new candidate, because the
 * dates and the notes of the first attempt are worth keeping unmuddled.
 */
export const CANDIDATE_STAGES = [
    'applied',
    'screening',
    'interview',
    'offer',
    'hired',
    'rejected',
];
export const isOpenCandidateStage = (stage) => stage !== 'hired' && stage !== 'rejected';
export const createCandidateSchema = z.object({
    openingId: objectId,
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().email().max(200),
    phone: z.string().trim().max(40).optional(),
    source: z.string().trim().max(80).optional(),
    stage: z.enum(CANDIDATE_STAGES).default('applied'),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    note: z.string().trim().max(4000).optional(),
    ownerId: objectId.nullable().optional(),
    resumeFileId: objectId.nullable().optional(),
});
export const updateCandidateSchema = createCandidateSchema.omit({ openingId: true }).partial();
export const moveCandidateSchema = z.object({
    stage: z.enum(CANDIDATE_STAGES),
    /** Asked for when rejecting, while the reason is still in somebody's head. */
    rejectedReason: z.string().trim().max(300).optional(),
});
export const candidateQuerySchema = z.object({
    openingId: objectId.optional(),
    stage: z.enum(CANDIDATE_STAGES).optional(),
    view: z.enum(['open', 'all']).default('open'),
});
/* ------------------------------ performance ------------------------------ */
export const CYCLE_STATUSES = ['draft', 'open', 'closed'];
export const createCycleSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    periodStart: dateOnly,
    periodEnd: dateOnly,
    dueDate: dateOnly,
    note: z.string().trim().max(2000).optional(),
})
    .refine((body) => body.periodEnd >= body.periodStart, 'A cycle cannot end before it starts');
export const updateCycleSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    dueDate: dateOnly.optional(),
    note: z.string().trim().max(2000).optional(),
});
/**
 * How far one review has got.
 *
 * Both halves are written independently and neither can see the other until
 * the manager shares it — which is the whole reason the states are ordered
 * this way rather than being two booleans. `shared` is the only state in which
 * the subject can read the manager's words.
 */
export const REVIEW_STATUSES = [
    'pending',
    'self-submitted',
    'manager-submitted',
    'shared',
];
export const GOAL_STATUSES = ['on-track', 'at-risk', 'done', 'dropped'];
const goalSchema = z.object({
    title: z.string().trim().min(2).max(200),
    status: z.enum(GOAL_STATUSES).default('on-track'),
    note: z.string().trim().max(500).optional(),
});
export const selfReviewSchema = z.object({
    rating: z.number().int().min(1).max(5),
    comment: z.string().trim().min(1).max(8000),
    goals: z.array(goalSchema).max(20).optional(),
});
export const managerReviewSchema = z.object({
    rating: z.number().int().min(1).max(5),
    comment: z.string().trim().min(1).max(8000),
    goals: z.array(goalSchema).max(20).optional(),
});
export const reviewQuerySchema = z.object({
    cycleId: objectId.optional(),
    status: z.enum(REVIEW_STATUSES).optional(),
    /** `team` is the manager's queue: the reviews they owe. */
    view: z.enum(['all', 'team']).default('all'),
});
